import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuthStore } from '../store/authStore'
import { findPageForAction } from '../lib/permissions'
import { useCurrentUserPagePermissions } from '../lib/permissions/usePagePermissions'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

export type PermissionLevel = 'none' | 'view' | 'edit' | 'full'

export type ViewScope = 'all' | 'own' | 'none'

export const ALL_MODULES = [
  'dashboard', 'pos', 'sales', 'inventory', 'stock', 'locations',
  'purchases', 'expenses', 'contacts', 'payroll', 'accounting',
  'reports', 'budget', 'notifications', 'audit', 'barcode',
  'end-of-day', 'settings', 'subscription', 'school',
] as const

export type ModuleKey = typeof ALL_MODULES[number]

interface FeaturePermissions {
  view?: boolean
  create?: boolean
  edit?: boolean
  delete?: boolean
  approve?: boolean
  export?: boolean
  process?: boolean
  pay?: boolean
  import?: boolean
  [key: string]: boolean | undefined
}

interface JsonbPermissions {
  all?: boolean
  [feature: string]: FeaturePermissions | boolean | undefined
}

interface RoleData {
  name: string
  permissions: JsonbPermissions
  is_system: boolean
}

// Role defaults (owner/admin always get 'full' – handled in canAccess)
const ROLE_DEFAULTS: Record<string, Record<string, PermissionLevel>> = {
  manager: {
    dashboard: 'full', pos: 'full', sales: 'full', inventory: 'full',
    stock: 'full', locations: 'view', purchases: 'full', expenses: 'full',
    contacts: 'full', payroll: 'view', accounting: 'view', reports: 'view',
    budget: 'view', notifications: 'full', audit: 'view', barcode: 'full',
    'end-of-day': 'full', settings: 'none', subscription: 'none', school: 'view',
  },
  cashier: {
    dashboard: 'view', pos: 'full', sales: 'view', inventory: 'view',
    stock: 'none', locations: 'none', purchases: 'none', expenses: 'none',
    contacts: 'view', payroll: 'none', accounting: 'none', reports: 'none',
    budget: 'none', notifications: 'view', audit: 'none', barcode: 'full',
    'end-of-day': 'full', settings: 'none', subscription: 'none', school: 'none',
  },
  accountant: {
    dashboard: 'view', pos: 'none', sales: 'view', inventory: 'view',
    stock: 'view', locations: 'none', purchases: 'view', expenses: 'full',
    contacts: 'view', payroll: 'full', accounting: 'full', reports: 'full',
    budget: 'full', notifications: 'view', audit: 'view', barcode: 'none',
    'end-of-day': 'view', settings: 'none', subscription: 'none', school: 'none',
  },
  viewer: {
    dashboard: 'view', pos: 'none', sales: 'view', inventory: 'view',
    stock: 'view', locations: 'view', purchases: 'view', expenses: 'view',
    contacts: 'view', payroll: 'none', accounting: 'view', reports: 'view',
    budget: 'view', notifications: 'view', audit: 'none', barcode: 'none',
    'end-of-day': 'none', settings: 'none', subscription: 'none', school: 'view',
  },
}

function levelValue(level: PermissionLevel): number {
  return { none: 0, view: 1, edit: 2, full: 3 }[level] ?? 0
}

export function usePermissions() {
  const { tenant, role, user } = useAuthStore()
  const tid = tenant?.id ?? ''
  const uid = user?.id ?? ''

  const isOwner = role === 'owner'
  const isAdmin = role === 'admin'
  const isSuperAdmin = role === 'super_admin'

  // Page-level permissions for the current user — needed for the joint
  // enforcement rule below (an action requires BOTH page-edit access AND the
  // action grant). For owner / super_admin this resolves every page to 'edit'.
  const pagePerms = useCurrentUserPagePermissions()

  // JSONB-based role permissions from roles table
  const { data: jsonbRoleData } = useQuery<RoleData | null>({
    queryKey: ['jsonb-role-permissions', tid, role],
    enabled: !!tid && !!role && !isOwner && !isAdmin,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await db('roles')
        .select('name, permissions, is_system')
        .eq('tenant_id', tid)
        .ilike('name', role ?? '')
        .maybeSingle()
      return data ?? null
    },
  })

  // Granular role_permissions (new system)
  const { data: grantedPermissions = [] } = useQuery<string[]>({
    queryKey: ['role-permissions', tid, uid],
    enabled: !!tid && !!uid && !isOwner && !isAdmin,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const { data: tu } = await db('tenant_users')
        .select('role_id')
        .eq('tenant_id', tid)
        .eq('user_id', uid)
        .maybeSingle()

      if (!tu?.role_id) return []

      const { data } = await db('role_permissions')
        .select('permission')
        .eq('role_id', tu.role_id)
        .eq('granted', true)

      return (data ?? []).map((r: { permission: string }) => r.permission)
    },
  })

  // Legacy: custom_role_permissions (old system fallback)
  const { data: customPerms = [] } = useQuery({
    queryKey: ['custom-role-permissions', tid, role],
    enabled: !!tid && !!role && !isOwner && !isAdmin,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const { data } = await db('custom_role_permissions')
        .select('module, permission_level')
        .eq('tenant_id', tid)
        .eq('role', role)
      return data ?? []
    },
  })

  const { data: overrides = [] } = useQuery({
    queryKey: ['user-permission-overrides', tid, uid],
    enabled: !!tid && !!uid,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const { data } = await db('user_permission_overrides')
        .select('module, permission_level')
        .eq('tenant_id', tid)
        .eq('user_id', uid)
      return data ?? []
    },
  })

  const { data: toggles = [] } = useQuery({
    queryKey: ['module-toggles', tid],
    enabled: !!tid,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const { data } = await db('module_toggles')
        .select('module, is_enabled')
        .eq('tenant_id', tid)
      return data ?? []
    },
  })

  // New granular permission check — joint enforcement.
  //   allowed = isSuperAdmin || isOwner || isAdmin
  //          || ( pageLevel === 'edit' && grantedPermissions.has(action) )
  // Super admin, owner and admin bypass everything. There is no code-level
  // block on sensitive actions — the owner decides who gets them via the role
  // editor. For every other role, a page-scoped action additionally requires
  // the parent page to be at 'edit' level (segregation of duties).
  function can(permission: string): boolean {
    if (isSuperAdmin || isOwner) return true
    if (isAdmin) return true

    // Joint rule: page-scoped actions require edit access on the parent page.
    // Fail-closed — a page that does not resolve to 'edit' (including a missing
    // row, which getLevel returns as 'none') denies the action outright.
    // Migration 202 backfills missing rows to 'none' so this is explicit.
    const pageKey = findPageForAction(permission)
    if (pageKey && pagePerms.getLevel(pageKey) !== 'edit') {
      return false
    }

    return grantedPermissions.includes(permission)
  }

  // Legacy module-level permission
  function getPermission(module: string): PermissionLevel {
    if (isOwner || isAdmin) return 'full'

    const override = overrides.find((o: { module: string; permission_level: PermissionLevel }) => o.module === module)
    if (override) return override.permission_level

    const custom = customPerms.find((p: { module: string; permission_level: PermissionLevel }) => p.module === module)
    if (custom) return custom.permission_level

    if (role && ROLE_DEFAULTS[role]) {
      return ROLE_DEFAULTS[role][module] ?? 'none'
    }

    return 'none'
  }

  function isModuleEnabled(module: string): boolean {
    const toggle = toggles.find((t: { module: string; is_enabled: boolean }) => t.module === module)
    return toggle ? toggle.is_enabled : true
  }

  function canAccess(module: string, required: PermissionLevel = 'view'): boolean {
    if (!isModuleEnabled(module)) return false
    return levelValue(getPermission(module)) >= levelValue(required)
  }

  const canAny = (...perms: string[]): boolean => perms.some(p => can(p))

  // Row-visibility scope for a module's list pages. 'all' sees every tenant row,
  // 'own' only rows the user created, 'none' nothing. Fail-closed: a role with
  // neither view_all nor view_own grant resolves to 'none'. Mirrors the
  // migration 227 RLS policy so the UI and DB agree.
  function viewScope(module: string): ViewScope {
    if (isSuperAdmin || isOwner || isAdmin) return 'all'
    if (grantedPermissions.includes(`${module}.view_all`)) return 'all'
    if (grantedPermissions.includes(`${module}.view_own`)) return 'own'
    return 'none'
  }

  // JSONB-based feature permissions (new system)
  const jsonbPerms = (jsonbRoleData?.permissions ?? {}) as JsonbPermissions

  const hasFeaturePerm = (feature: string, action: string): boolean => {
    if (isOwner || isAdmin || jsonbRoleData?.permissions?.all) return true
    const feat = jsonbPerms[feature] as FeaturePermissions | undefined
    if (!feat) return false
    if (action !== 'view' && !feat.view) return false
    return feat[action] ?? false
  }

  const canView = (feature: string) => hasFeaturePerm(feature, 'view')
  const canCreate = (feature: string) => hasFeaturePerm(feature, 'create')
  const canEdit = (feature: string) => hasFeaturePerm(feature, 'edit')
  const canDelete = (feature: string) => hasFeaturePerm(feature, 'delete')
  const canApprove = (feature: string) => hasFeaturePerm(feature, 'approve')
  const canExport = (feature: string) => hasFeaturePerm(feature, 'export')

  return {
    can, canAny, viewScope, getPermission, canAccess, isModuleEnabled,
    customPerms, overrides, toggles, grantedPermissions, isOwner, isAdmin,
    canView, canCreate, canEdit, canDelete, canApprove, canExport,
    jsonbRoleData,
  }
}
